import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../Helpers/Handler";
import "./RingNow.css";

// "Ring now" (Notification Centre redesign, R2). The same file is used by the admin and the staff web;
// only the two URLs differ.
//   pending task  -> rings every eligible staff member (or the ones chosen); the first to accept gets it
//   assigned task -> rings the assignee until they tap Acknowledge
// The server allows one ring per task every two minutes; a ring with no answer after two minutes tells
// the admins (and the staff member who rang).
const NOTE_MAX = 140;

const STATE = {
    ringing: { tone: "wait", label: "Ringing" },
    accepted: { tone: "ok", label: "Accepted" },
    acknowledged: { tone: "ok", label: "Acknowledged" },
    no_answer: { tone: "bad", label: "No answer" },
    too_late: { tone: "neutral", label: "Someone else took it" },
};
const Chip = ({ state }) => {
    const meta = STATE[state] || { tone: "neutral", label: state };
    return <span className={`rn-chip rn-chip-${meta.tone}`}>{meta.label}</span>;
};
const mmss = (sec) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;

const RingNowDialog = ({ taskId, statusUrl, ringUrl, onClose, onChanged }) => {
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [who, setWho] = useState("all");
    const [chosen, setChosen] = useState([]);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [tick, setTick] = useState(0); // seconds since the last load, for the countdowns
    const closeRef = useRef(null);

    const load = useCallback(async (quiet) => {
        if (!quiet) setLoading(true);
        const response = await Handler({ method: "get", url: statusUrl });
        if (response?.success) {
            setInfo(response?.data?.data || null);
            setError("");
        } else if (!quiet) {
            setError(response?.data?.message || "Could not load this task");
        }
        setTick(0);
        setLoading(false);
    }, [statusUrl]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        document.addEventListener("keydown", onKey);
        closeRef.current?.focus();
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    // While a ring is live (or the cooldown runs) count down every second and look again every 5 s.
    const live = info?.ring?.status === "ringing" || (info?.cooldownLeftSec || 0) > 0;
    useEffect(() => {
        if (!live) return undefined;
        const t = setInterval(() => setTick((n) => n + 1), 1000);
        return () => clearInterval(t);
    }, [live]);
    useEffect(() => {
        if (live && tick > 0 && tick % 5 === 0) load(true);
        if (live && info && tick >= Math.max(info.ring?.secondsLeft || 0, info.cooldownLeftSec || 0) + 1) load(true);
    }, [tick, live, info, load]);

    const ring = async () => {
        if (info?.mode === "offer" && who === "some" && !chosen.length) {
            toast.error("Choose at least one person, or ring everyone eligible");
            return;
        }
        setBusy(true);
        const data = { note: note.trim() };
        if (info?.mode === "offer" && who === "some") data.staffIds = chosen;
        const response = await Handler({ method: "post", url: ringUrl, data });
        if (response?.success) {
            toast.success(response?.data?.message || "Ringing");
            setInfo(response?.data?.data || null);
            setTick(0);
            setNote("");
            onChanged?.();
        } else {
            toast.error(response?.data?.message || "Could not ring");
            load(true);
        }
        setBusy(false);
    };

    const toggle = (id) => setChosen((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));
    const cooldown = Math.max(0, (info?.cooldownLeftSec || 0) - tick);
    const ringLeft = Math.max(0, (info?.ring?.secondsLeft || 0) - tick);
    const r = info?.ring;
    const ringStatus = r?.status === "ringing" && ringLeft === 0 ? "no_answer" : r?.status;
    const canRing = !!info?.mode && cooldown === 0 && (info.mode === "ack" || (info.eligible || []).length > 0);
    const reason = !info?.mode || (info.mode === "offer" && !(info.eligible || []).length) ? info?.reason : cooldown ? `You can ring again in ${mmss(cooldown)}.` : "";

    return (
        <div className="rn-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
            <div className="rn-dialog" role="dialog" aria-modal="true" aria-labelledby="rn-title">
                <div className="rn-head">
                    <div>
                        <div className="rn-sub">Task {taskId}</div>
                        <h2 id="rn-title" className="rn-title">Ring now</h2>
                    </div>
                    <button type="button" ref={closeRef} className="rn-x" onClick={onClose} aria-label="Close">×</button>
                </div>

                {loading ? <div className="rn-sub py-3">Loading...</div> : null}
                {!loading && error ? <div className="rn-alert rn-alert-bad">{error}</div> : null}

                {!loading && info ? (
                    <>
                        {info.mode === "offer" ? (
                            <p className="rn-text">This task has no one on it. Their phones ring like an alarm, even when locked; the first to accept gets it.</p>
                        ) : info.mode === "ack" ? (
                            <p className="rn-text"><strong>{info.assignee?.name}</strong> has this task. Their phone rings until they tap <strong>Acknowledge</strong>, so you know they are on it.</p>
                        ) : null}

                        {info.mode === "offer" && (info.eligible || []).length ? (
                            <fieldset className="rn-group">
                                <legend className="rn-label">Who to ring</legend>
                                <label className="rn-radio">
                                    <input type="radio" name="rn-who" checked={who === "all"} onChange={() => setWho("all")} />
                                    <span>Everyone who can take it <span className="rn-sub">({info.eligible.length})</span></span>
                                </label>
                                <label className="rn-radio">
                                    <input type="radio" name="rn-who" checked={who === "some"} onChange={() => setWho("some")} />
                                    <span>Only the people I choose</span>
                                </label>
                                {who === "some" ? (
                                    <div className="rn-people">
                                        {info.eligible.map((s) => (
                                            <label key={s.staffId} className="rn-person">
                                                <input type="checkbox" checked={chosen.includes(s.staffId)} onChange={() => toggle(s.staffId)} />
                                                <span>{s.name}</span><span className="rn-sub">{s.staffId}</span>
                                            </label>
                                        ))}
                                    </div>
                                ) : null}
                                <div className="rn-sub">Only staff at this hub who do this kind of task, are checked in and are free around then.</div>
                            </fieldset>
                        ) : null}

                        {info.mode ? (
                            <div className="rn-group">
                                <label className="rn-label" htmlFor="rn-note">Note <span className="rn-sub">(optional)</span></label>
                                <textarea id="rn-note" className="form-control" rows={2} maxLength={NOTE_MAX} value={note} placeholder="Customer is waiting at the gate" onChange={(e) => setNote(e.target.value)} />
                                <div className="rn-sub text-end">{note.length}/{NOTE_MAX}</div>
                            </div>
                        ) : null}

                        {reason ? <div className={`rn-alert ${info.mode ? "rn-alert-wait" : "rn-alert-bad"}`}>{reason}</div> : null}

                        {r?.mode ? (
                            <div className="rn-ring">
                                <div className="rn-ring-head">
                                    <strong>Last ring</strong>
                                    <Chip state={ringStatus} />
                                    {ringStatus === "ringing" ? <span className="rn-count" aria-live="polite">{mmss(ringLeft)} left</span> : null}
                                </div>
                                <div className="rn-sub">
                                    By {r.by?.name || "the office"} · {r.mode === "offer" ? "to take the task" : "to acknowledge"}
                                    {r.note ? <> · “{r.note}”</> : null}
                                </div>
                                {r.acceptedBy ? <div className="rn-text mt-1"><strong>{r.acceptedBy.name}</strong> took the task.</div> : null}
                                <ul className="rn-list">
                                    {(r.staff || []).map((s) => (
                                        <li key={s.staffId}><span>{s.name}</span><Chip state={ringStatus === "no_answer" && s.state === "ringing" ? "no_answer" : s.state} /></li>
                                    ))}
                                </ul>
                                {ringStatus === "no_answer" ? <div className="rn-sub">No answer in two minutes. The admins were told. Ring again, or assign someone.</div> : null}
                            </div>
                        ) : null}
                    </>
                ) : null}

                <div className="rn-actions">
                    <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>Close</button>
                    {info?.mode ? (
                        <button type="button" className="btn btn-primary" onClick={ring} disabled={busy || !canRing}>
                            {busy ? "Ringing..." : info.mode === "offer" ? (who === "some" ? (chosen.length ? `Ring ${chosen.length} chosen` : "Ring chosen") : `Ring ${info.eligible?.length || 0} staff`) : `Ring ${info.assignee?.name || "the assignee"}`}
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default RingNowDialog;
